/**
 * SettingsPanel — player-controlled UI, accessibility and Pre-Alpha
 * title visibility. Persists per-character server-side through
 * PATCH /api/character/preferences and POST /api/character/pre-alpha/visibility.
 *
 * Applies changes live to the DOM via the `useAccessibility` effect
 * (see /app/frontend/src/lib/accessibility.js).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import LanguageSelector from "@/components/LanguageSelector";
import { X, Eye, Palette, Type, Zap, Award, Sparkles, Globe } from "lucide-react";

const COLOR_BLIND_MODES = [
  { id: "none",         label: "Nessuna" },
  { id: "protanopia",   label: "Protanopia (rosso→arancio)" },
  { id: "deuteranopia", label: "Deuteranopia (verde→arancio)" },
  { id: "tritanopia",   label: "Tritanopia (blu→verde)" },
];
const TEXT_SIZES = [
  { id: "sm", label: "Piccolo" },
  { id: "md", label: "Normale" },
  { id: "lg", label: "Grande" },
];

export default function SettingsPanel({ ch, onClose, onUpdated }) {
  const { t } = useTranslation();
  const prefs = ch?.preferences || {};
  const a11y = prefs.accessibility || {};

  const [heterochromia, setHeterochromia] = useState(prefs.heterochromia_enabled !== false);
  const [highContrast, setHighContrast] = useState(!!a11y.high_contrast);
  const [reduceMotion, setReduceMotion] = useState(!!a11y.reduce_motion);
  const [reduceEffects, setReduceEffects] = useState(!!a11y.reduce_effects);
  const [colorBlind, setColorBlind] = useState(a11y.color_blind_mode || "none");
  const [textSize, setTextSize] = useState(a11y.text_size || "md");
  const [useShapes, setUseShapes] = useState(!!a11y.use_shapes);
  const [preAlphaShow, setPreAlphaShow] = useState(
    prefs.pre_alpha_title_visible !== false
  );
  const [busy, setBusy] = useState(false);

  const preAlphaEligible = !!ch?.pre_alpha_eligible;

  // Persist any single change immediately so the setting survives a refresh
  // even if the modal is closed mid-flow.
  const persist = async (patch) => {
    setBusy(true);
    try {
      const res = await api.patchPreferences(patch);
      onUpdated?.({ preferences: res.preferences });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile salvare");
    } finally { setBusy(false); }
  };

  const togglePreAlpha = async (val) => {
    if (!preAlphaEligible) return;
    setPreAlphaShow(val);
    setBusy(true);
    try {
      await api.togglePreAlphaTitle(val);
      // Also mirror onto preferences so the server-side helper picks it up
      onUpdated?.({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile salvare");
      setPreAlphaShow(!val);   // rollback
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center px-3"
      style={{ background: "rgba(5,5,5,0.86)", backdropFilter: "blur(2px)" }}
      onClick={onClose}
      data-testid="settings-panel"
    >
      <div
        className="panel p-4 max-w-md w-full max-h-[85vh] overflow-y-auto space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-[#262626] pb-2">
          <Sparkles className="w-4 h-4 text-[#E5C34A]" />
          <div className="font-heading tracking-[0.3em] uppercase text-[#E5C34A] text-sm flex-1">
            {t("settings.title")}
          </div>
          <button data-testid="settings-close" onClick={onClose}
            className="text-[#8c8c8c] hover:text-[#E2DFD8]">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Lingua — top priority per user's request (Feb 2026 i18n rollout) */}
        <Section icon={Globe} title={t("settings.language")}>
          <LanguageSelector variant="full" />
        </Section>

        {/* Eterocromia */}
        <Section icon={Eye} title={t("settingsFull.appearance")}>
          <Toggle testid="pref-heterochromia" label={t("settingsFull.heterochromia")}
            checked={heterochromia}
            onChange={v => { setHeterochromia(v); persist({ heterochromia_enabled: v }); }}
            busy={busy} />
        </Section>

        {/* Accessibilità visiva */}
        <Section icon={Palette} title={t("settingsFull.visualA11y")}>
          <Select testid="pref-color-blind" label={t("settingsFull.colorBlindMode")}
            value={colorBlind} options={COLOR_BLIND_MODES}
            onChange={v => { setColorBlind(v); persist({ accessibility: { color_blind_mode: v } }); }} />
          <Toggle testid="pref-high-contrast" label={t("settingsFull.highContrast")}
            checked={highContrast}
            onChange={v => { setHighContrast(v); persist({ accessibility: { high_contrast: v } }); }}
            busy={busy} />
          <Toggle testid="pref-use-shapes" label={t("settingsFull.useShapes")}
            checked={useShapes}
            onChange={v => { setUseShapes(v); persist({ accessibility: { use_shapes: v } }); }}
            busy={busy} />
        </Section>

        {/* Testo */}
        <Section icon={Type} title={t("settingsFull.text")}>
          <Select testid="pref-text-size" label={t("settingsFull.textSize")}
            value={textSize} options={TEXT_SIZES}
            onChange={v => { setTextSize(v); persist({ accessibility: { text_size: v } }); }} />
        </Section>

        {/* Movimento / effetti */}
        <Section icon={Zap} title={t("settingsFull.visualFx")}>
          <Toggle testid="pref-reduce-motion" label={t("settingsFull.reduceMotion")}
            checked={reduceMotion}
            onChange={v => {
              setReduceMotion(v);
              persist({ accessibility: { reduce_motion: v },
                        visual_effects: { reduced_motion: v } });
            }}
            busy={busy} />
          <Toggle testid="pref-reduce-effects" label={t("settingsFull.reduceEffects")}
            checked={reduceEffects}
            onChange={v => {
              setReduceEffects(v);
              persist({ accessibility: { reduce_effects: v },
                        visual_effects: { flash_effects: !v, combat_particles: !v, screen_shake: !v } });
            }}
            busy={busy} />
        </Section>

        {/* Pre-Alpha Tester title */}
        {preAlphaEligible && (
          <Section icon={Award} title={t("settingsFull.preAlphaTitle")} gold>
            <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c]">
              {t("settingsFull.preAlphaHint")}
            </div>
            <Toggle testid="pref-pre-alpha"
              label={<span className="font-heading tracking-widest uppercase text-[#E5C34A]">{t("settingsFull.preAlphaShow")}</span>}
              checked={preAlphaShow}
              onChange={togglePreAlpha}
              busy={busy} />
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, gold, children }) {
  return (
    <div className={`space-y-2 border ${gold ? "border-[#B89C3A]" : "border-[#262626]"} p-2`}>
      <div className={`font-heading text-[10px] tracking-widest uppercase flex items-center gap-1 ${gold ? "text-[#E5C34A]" : "text-[#8c8c8c]"}`}>
        <Icon className="w-3 h-3" /> {title}
      </div>
      {children}
    </div>
  );
}

function Toggle({ label, checked, onChange, testid, busy }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer text-[11px] text-[#B0ADA5]">
      <input type="checkbox" checked={checked} disabled={busy}
        onChange={e => onChange(e.target.checked)}
        data-testid={testid}
        className="accent-[#B89C3A]" />
      <span>{label}</span>
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mb-1">{label}</div>
      <select value={value} onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-1 text-xs text-[#E2DFD8] focus:border-[#B89C3A] outline-none">
        {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </label>
  );
}
